/** @module csi/table/_DndGalleryMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-class",
	"csi/gallery/_base",
	"csi/heading/_base",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/view/_DndMixin",
	"csi/xml"
], function(declare, lang, aspect, domClass, {getGroupColumn, getGroupContentNodes, getGroupRow}, csiHeading, {hasAggregateStoreForAttribute}, reflectionHints, _DndMixin, xml) {

	/**
	 * Need to determine if attribute is inline editable in current context
	 * @param {Context} ctx
	 * @param {Spec} spec
	 * @return {boolean}
	 */
	function _canEditSpecInContext(ctx, spec) {
		return !spec || csiHeading.applyHeadingRenderer(ctx, spec.heading, (renBase, hBase) => renBase.canEditByDragAndDrop(ctx, hBase));
	}

	/**
	 * Return selected node in source
	 * @param {DndSource} source 
	 * @return {HTMLElement}
	 */
	function _getSourceSingleSelection(source) {
		const id = Object.keys(source.selection)[0];
		return source.node.querySelector(`#${id}`);
	}

	/**
	 * Return renderer store for given context and spec
	 * @param {Context} ctx 
	 * @param {Spec} spec 
	 * @return {StoreNames}
	 */
	function _getSpecStore(ctx, spec) {
		return spec && csiHeading.applyHeadingRenderer(ctx, spec.heading, (renBase, hBase) => renBase.getStore?.(ctx, hBase));
	}

	/**
	 * Need to determine if spec attribute has appropriate conditions for dragging
	 * @param {Spec} spec
	 * @return {boolean}
	 */
	function _isSpecAppropriate(spec) {
		// only validate if there is a spec for column or row grouping
		if (!spec) {
			return true;
		}

		// drag and drop is only allowed if:
		// 1. delayed search is not in use - client will need to know all possible values which is not feasible when using delayed search (unless do multiple queries)
		// 2. attribute has no special operation logic for changing the attribute on the client, including watchAttribute;
		// 		this is just a first step - we could potentially support some special logic later, but for now start without it
		// 3. only allow refs with an aggregate store (that can correctly identify available values and the proper update operation to use)
		const {heading, ra, rc, type} = spec;
		const canFindAvailableValues = (type !== xml.ATTR_REF) || hasAggregateStoreForAttribute(rc.type, ra.attributeId);

		return ra &&
			!ra.isDelayedSearch &&
			canFindAvailableValues &&
			!reflectionHints.getWatchFunction(rc.type, heading.m_id) &&
			!heading.m_action?.hasSpecialLogic();
	}

	return declare([_DndMixin], {
		/** @property {StoreNames} _selectionColumnStore - aggregate store for column attribute of current selection */
		_selectionColumnStore: null,

		/** @property {StoreNames} _selectionRowStore - aggregate store for row attribute of current selection */
		_selectionRowStore: null,

		/**
		 * _DnDHandler[]: available DnD handlers for Gallery views
		 * allows reordering of items in ordered gallery if user has write access to container, the relationship is ordered, and view is not sorted or filtered
		 */
		dndHandlers: [
			{
				// allows moving items from one group to another if view is grouped and not read-only
				controller: "csi/action/DndGroupedController",
				css: "csiDndGrouped",
				dropInFront: true,

				canDrag(ctx) {
					return _canEditSpecInContext(ctx, ctx.view._groupColSpec) && _canEditSpecInContext(ctx, ctx.view._groupRowSpec);
				},

				isAppropriateFor(view) {
					return view.bPlot &&
						view.expandAllGroups &&
						!view.groupItemsPerRow &&
						!view.getContext().isReadOnly() &&
						_isSpecAppropriate(view._groupColSpec) &&
						_isSpecAppropriate(view._groupRowSpec);
				}
			},
			{
				// allows reordering of items in ordered gallery if user has write access to container, the relationship is ordered, and view is not sorted or filtered
				allowMultiple: true,
				controller: "csi/action/DndOrderedController",
				css: "csiDndOrdered",
				dropInFront: false,

				canDrag: function(ctx) {
					return true;
				},

				isAppropriateFor: function(view) {
					const qr = view.getQueryResult();
					const {bOrdered} = qr.level.childLevel;

					if (!bOrdered || qr.isFiltered() || qr.isSorted()) {
						return false;
					}

					return !view.bPlot && view.getContext().hasParentAccess(xml.RIGHT_WRITE);
				}
			}
		],

		/**
		 * Over rides avatar creation to restructure the gallery avatar
		 * Attention: If you use this module, and hold position of your dnd avatar does not match cursor position exactly, then 
		 * please add class '.csi-dnd-avatar' to the element which upper left corner exactly match dnd avatar's upper left corner
		 * @param {DomNode} domNode
		 * @param {DomNode} item
		 */
		getFormattedAvatar: function(domNode, item) {
			// Position dnd avatar against the cursor so that it will be held at drag anchor
			var jItem = $(item);
			var jGalleryImgNode = $(item).find('.image');
			var dndAnchor = jItem.find(".dojoDndHandle")[0];

			$(domNode)
				.height(jItem.height())
				.width(jItem.width())
				.css({
					position: 'relative',
					top: - dndAnchor.offsetHeight / 2 - dndAnchor.offsetTop + 'px',
					left: - dndAnchor.offsetWidth / 2 - dndAnchor.offsetLeft + 'px'
				});

			// Prevent avatar's card image from collapsing to smaller size: set it width and height 
			$(domNode)
				.find('.image')
				.height(jGalleryImgNode.height())
				.width(jGalleryImgNode.width());

			return domNode;
		},

		/**
		 * Return true if view explicitly restricts Dnd, or template does not contain appropriate DnD class
		 */
		isDndAvailable: function() {
			return !this.dndRestrict && domClass.contains(this.blockNode, "dojoDndItem");
		},

		/**
		 * Determine if blocks in view are oriented vertically / horizontal which allows CSS to better reflect drop zone
		 */
		isDndHorizontal: function() {
			return !domClass.contains(this.blockNode, "csiDndVertical");
		},

		/**
		 * Add aspects to control Dnd behavior
		 */
		postCreate: function() {
			var self = this;
			this.inherited(arguments);

			this.own(
				aspect.before(this, "_buildViewResults", lang.hitch(this, function(results, bRefresh) {
					//cleanup and recreate dndSource - only do this when results are being refreshed (not added incrementally)
					if (bRefresh) {
						self._dndInitialize(results, bRefresh);
					}
				})),
				aspect.around(this, "_toHtmlResult", function(originalFunction) {
					return function(ctx, refNodeParent, refNode, refNodeNext, actions, bNew) {
						refNode = originalFunction.apply(this, arguments);
						this._dndBuildResult(refNode);
						return refNode;
					};
				})
			);
		},

		/**
		 * Initialize dndSource 
		 * @param {QueryResults} [results]
		 * @param {Boolean} [bRefresh]
		 * @param {Query} qrParent
		 */
		_dndInitialize: function(results, bRefresh) {
			this.inherited(arguments);

			// add DnD action to available actions
			var ql = this.getMainQueryLevel();
			var qlActions = ql.actions;

			if ((!qlActions || !qlActions.getAction("Dnd")) && this._dndHandler) {
				var actions = qlActions ? qlActions.getActions() : [];

				ql.actions = lang.mixin(new this.actionsClass(), {
					m_actions: [this._dndAction].concat(actions),
					m_class: actions ? actions.m_class : null,
					m_rules: actions ? actions.m_rules : null
				});
			}
		},

		/**
		 * Returns the drag and drop root node
		 * Property this.bPlot distinguish grouped (bPlot== true) and not grouped galleries (bPlot == undefined). 
		 * Grouped gallery supposed to have multiple dnd containers (each group), 
		 * where non grouped only has one container - Content Root 
		*/
		getDndSourceNodes: function() {
			return this.bPlot ? getGroupContentNodes(this.plotNode) : [this.getContentRoot()];
		},

		/**
		 * @override
		 * Set acceptance of other sources based on item being dragged
		 */
		async onDndStartExtension(source) {
			const {
				_dndSources,
				_groupColSpec,
				_groupColValues,
				_groupRowSpec,
				_groupRowValues,
			} = this;

			// get aggregate stores for grouping attributes, if any
			const refNode = _getSourceSingleSelection(source);
			const qr = this.getQueryResultByDomNode(refNode);
			const ctx = qr.getContext();
			const columnStore = this._selectionColumnStore = _getSpecStore(ctx, _groupColSpec);
			const rowStore = this._selectionRowStore = _getSpecStore(ctx, _groupRowSpec);
			const hasStores = !!columnStore || !!rowStore;

			// set acceptance for all DnD sources before querying - if there are aggregate stores, the only the starting source is acceptable to drop the selected node
			// while the stores are being queried; if no aggregate stores, then any source is acceptable
			_dndSources.forEach((s) => {
				s.setTargetState(!hasStores || (s === source));
			});

			if (hasStores) {
				// query stores to determine available values
				await Promise.all([
					columnStore?.query(),
					rowStore?.query()
				]);

				// set appropriate acceptance for all DnD sources based on the values of that source and the available values of the aggregate stores
				const sourceColumn = getGroupColumn(source.node);
				const sourceRow = getGroupRow(source.node);

				_dndSources.forEach((s) => {
					const targetColumn = getGroupColumn(s.node);
					const targetRow = getGroupRow(s.node);
					const validColumn = (sourceColumn === targetColumn) || this._isDndSourceValidValue(targetColumn, _groupColSpec, _groupColValues, columnStore);
					const validRow = (sourceRow === targetRow) || this._isDndSourceValidValue(targetRow, _groupRowSpec, _groupRowValues, rowStore);
					s.setTargetState(validColumn && validRow);
				});
			}
		},

		/**
		 * Return value at given index in store
		 * @param {number} sourceIndex 
		 * @param {Spec} spec 
		 * @param {StoreValues} valuesStore 
		 * @param {StoreNames} aggregateStore 
		 * @return {C8AttributeValue | null}
		 */
		_getDndGroupedSourceValue(sourceIndex, spec, valuesStore, aggregateStore) {
			const indexQuery = {[valuesStore.indexProperty]: sourceIndex};
			let value;

			valuesStore.query(indexQuery, {useCache: true}).forEach((item) => {
				value = valuesStore.getIdentity(item);
			});

			// for ref attributes, we need to map the value (which is the name of the ref) to the URL;
			// if more than one URL has the same name, then we do not allow drag and drop to that source
			if (spec.type === xml.ATTR_REF) {
				const labelQuery = {[aggregateStore.labelProperty]: value};
				const results = aggregateStore.internalQuery(labelQuery);

				value = (results.length === 1) ? aggregateStore.getIdentity(results[0]) : null;
			}

			return value;
		},

		/**
		 * Return true if the value represented
		 * @param {number} sourceIndex 
		 * @param {Spec} spec 
		 * @param {StoreValues} valuesStore 
		 * @param {StoreNames} aggregateStore
		 * @return {boolean}
		 */
		_isDndSourceValidValue(sourceIndex, spec, valuesStore, aggregateStore) {
			// if no aggregate store, then assume any value is valid in this dimension
			if (!aggregateStore) {
				return true;
			}

			const value = this._getDndGroupedSourceValue(sourceIndex, spec, valuesStore, aggregateStore);

			// if value is not in store, then not a valid place to drop
			if ((value === null) || !aggregateStore.get(value)) {
				return false;
			}

			// do not allow resetting to empty when value is required
			return !spec.ra.isRequired || (value !== aggregateStore.getEmptyValue());
		}
	});
});
